import { datadogRum } from '@datadog/browser-rum';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import { useDappInfo } from '@/hooks/useDappInfo';
import { usePopupId } from '@/hooks/usePopupId';
import { useUser } from '@/hooks/useUser';
import { setGlobalDappId, setTraceId } from '@/utils/trpc';
import { Loading } from '@infinex-private/connect-web-shared/components';
import { useExtension } from '@infinex-private/connect-web-shared/providers';
import type { ChainValidationResponse } from '@infinex-private/connect-web-shared/types';
import type { User } from '@infinex/connect-service-contract/client';

export interface PopupContextType {
  user: User | null;
  invalidateUser: () => Promise<void>;
  clearUser: () => void;
  sendRequestProcessedNotification: (data?: any) => void;
  globalSessionId: string | null;
  dappId: string | null;
  popupId: string;
  dappUiName: string;
  dappIcon: string;
  dappOrigin: string | null;
  isLoading: boolean;
  popupData: any | null;
}

const PopupContext = createContext<PopupContextType | null>(null);

export function PopupProvider({ children }: { children: ReactNode }) {
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [popupData, setPopupData] = useState<ChainValidationResponse | null>(
    null
  );

  const { popupId } = usePopupId();
  const {
    isLoading: extensionIsLoading,
    sendNotification,
    sendRequest,
  } = useExtension();

  console.log('[PopupProvider] Initializing with popupId:', popupId);

  // Backwards compatibility: if appKey is in query params, fetch dapp info via API
  // Otherwise, use dappId from popupData (new flow)
  // TODO: remove this
  const { dappId: apiDappId } = useDappInfo();

  // Use popup data if available, otherwise use dapp info from API
  const dappId = popupData?.dappId ?? apiDappId;

  const {
    invalidateUser,
    clearUser: clearUser,
    user,
    globalSessionId,
    isLoading: isLoadingSession,
  } = useUser();

  // Fetch popup data and update initializing state
  useEffect(() => {
    console.log('[PopupProvider] Checking if ready to fetch popup data:', {
      extensionIsLoading,
      isLoadingSession,
      popupId,
      willFetch: !extensionIsLoading && !isLoadingSession && !!popupId,
    });

    if (extensionIsLoading || isLoadingSession || !popupId) return;

    const fetchPopupData = async () => {
      try {
        const data = await sendRequest('popup-info', 'get-popup-data', {
          popupId,
        });
        setPopupData(data);
      } catch (error) {
        console.error('[PopupProvider] Failed to fetch popup data:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    void fetchPopupData();
  }, [extensionIsLoading, isLoadingSession, popupId, sendRequest]);

  // Configures tRPC to pass the global dapp id in requests
  useEffect(() => {
    if (!dappId) return;
    setGlobalDappId(dappId);
  }, [dappId]);

  // Set global context for Datadog RUM
  useEffect(() => {
    if (user) {
      datadogRum.setUser({
        id: user.userId,
        name: user.username,
      });
    }

    if (dappId) {
      datadogRum.setGlobalContextProperty('dappId', dappId);
    }

    if (popupId) {
      datadogRum.setGlobalContextProperty('popupId', popupId);
    }

    if (popupData) {
      // Set trace ID header for all tRPC requests (correlates with extension logs)
      if (popupData.txnRequestId) {
        setTraceId(popupData.txnRequestId);
      }

      datadogRum.setGlobalContextProperty('popupData', popupData);
    }
  }, [user, dappId, popupId, popupData]);

  const sendRequestProcessedNotification = (data?: any) => {
    sendNotification('popup-info', 'request-processed', {
      popupId: popupId ?? '',
      response: data ?? null,
    });
  };

  const isLoading = isInitializing || !dappId;

  if (isLoading) {
    return <Loading />;
  }

  const contextValue: PopupContextType = {
    user,
    invalidateUser,
    clearUser,
    globalSessionId,
    dappId,
    popupId: popupId ?? '',
    dappUiName: popupData?.dappInfo?.name ?? '',
    dappIcon: popupData?.dappInfo?.icon ?? '',
    dappOrigin: popupData?.dappInfo?.hostname ?? '',
    sendRequestProcessedNotification,
    isLoading,
    popupData,
  };

  return (
    <PopupContext.Provider value={contextValue}>
      {children}
    </PopupContext.Provider>
  );
}

export function usePopup() {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error('usePopup must be used within a PopupProvider');
  }
  return context;
}
